import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { Pill, fmtDate, fmtRelative, label } from '@/features/dossier/components/primitives'
import { cancelDeployment, listLedger, resendDeployment, type Deployment } from './api'
import { StatusChip } from './components'

/**
 * The ledger — everything this room has ever said out loud.
 *
 * ## It is append-only in the database, not merely by convention
 *
 * `warroom_deployment` carries triggers that refuse UPDATE of the posted text and refuse DELETE
 * outright. So this page is a window onto a record that cannot be tidied afterwards, which is
 * the only version of "full audit of the war" that survives somebody wanting a bad row gone.
 *
 * ## Every row names the approver
 *
 * `approvedBy` is NOT NULL. There is no such thing here as a post that happened by itself, and
 * the column is rendered first for that reason.
 */
export function LedgerPage() {
  const authorities = useSession((s) => s.authorities)
  const canDeploy = hasPermission(authorities, 'warroom:deploy')

  const [rows, setRows] = useState<Deployment[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [draftQuery, setDraftQuery] = useState('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState({ pages: 1, elements: 0 })

  const load = useCallback(async () => {
    try {
      setError(null)
      const found = await listLedger({ q: query || undefined, page, size: 50 })
      setRows(found.content)
      setTotal({ pages: found.totalPages, elements: found.totalElements })
    } catch (err) {
      setError(explain(err))
    }
  }, [query, page])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading the ledger" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="The ledger"
        description="Every approval, the exact text that went out, and who put their name on it.
                     Rows cannot be edited or deleted — the database refuses."
      />

      {error && <ErrorStrip message={error} />}

      <SearchBar draft={draftQuery} onDraft={setDraftQuery} query={query}
                 onSearch={() => { setPage(0); setQuery(draftQuery) }}
                 onClear={() => { setDraftQuery(''); setQuery(''); setPage(0) }}
                 total={total.elements} unit="posts" />

      <Panel>
        <ErpTable head={<>
          <Th>Approved</Th>
          <Th>Text sent</Th>
          <Th className="axis-hide-sm">Account</Th>
          <Th className="axis-hide-sm">Tactic</Th>
          <Th>State</Th>
          <Th />
        </>}>
          {rows.length === 0 && (
            <EmptyRow colSpan={6}
                      message="Nothing has been sent. An empty ledger is a room that has not
                               spoken yet." />
          )}
          {rows.map((d) => (
            <Fragment key={d.id}>
              <tr className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setOpen(open === d.id ? null : d.id)}>
                <Td className="whitespace-nowrap">
                  <span className="text-[11px]">{fmtDate(d.approvedAt)}</span>
                  <span className="block text-[10px] text-muted-foreground">
                    by {d.approvedBy.slice(0, 8)}
                  </span>
                </Td>
                <Td>
                  <span className="line-clamp-2 text-[11px]">{d.postedBody}</span>
                </Td>
                <Td className="axis-hide-sm">
                  <span className="text-[11px]">@{d.accountHandle ?? '—'}</span>
                  {d.personaName && (
                    <span className="block text-[10px] text-muted-foreground">
                      {d.personaName}
                    </span>
                  )}
                </Td>
                <Td className="axis-hide-sm whitespace-nowrap">
                  {d.tacticCode && <Pill className="border-border">{label(d.tacticCode)}</Pill>}
                </Td>
                <Td><StatusChip code={d.statusCode} /></Td>
                <Td>
                  {canDeploy && d.statusCode === 'QUEUED' && (
                    <button type="button" disabled={busy === d.id}
                            onClick={(e) => {
                              e.stopPropagation()
                              setBusy(d.id)
                              void cancelDeployment(d.id)
                                .then(load)
                                .catch((err) => setError(explain(err)))
                                .finally(() => setBusy(null))
                            }}
                            className="whitespace-nowrap rounded border border-border px-2
                                       py-0.5 text-[10px]">
                      Stop it
                    </button>
                  )}
                  {/*
                    The counterpart to giving up. The dispatcher stops after a bounded number of
                    attempts precisely so a person decides whether another is sensible — which
                    needs a way to say yes. It never retries on its own past the ceiling.
                  */}
                  {canDeploy && d.statusCode === 'FAILED' && (
                    <button type="button" disabled={busy === d.id}
                            onClick={(e) => {
                              e.stopPropagation()
                              setBusy(d.id)
                              void resendDeployment(d.id)
                                .then(load)
                                .catch((err) => setError(explain(err)))
                                .finally(() => setBusy(null))
                            }}
                            className="whitespace-nowrap rounded border border-border px-2
                                       py-0.5 text-[10px]">
                      Send it again
                    </button>
                  )}
                </Td>
              </tr>
              {open === d.id && (
                <tr className="bg-muted/20">
                  <td colSpan={6} className="px-3 py-2">
                    <p className="whitespace-pre-wrap rounded border border-border
                                  bg-background p-2 text-[12px]">{d.postedBody}</p>
                    <dl className="mt-1.5 grid gap-x-4 gap-y-0.5 text-[10px] sm:grid-cols-3">
                      <div>
                        <dt className="text-muted-foreground">Scheduled</dt>
                        <dd>{fmtDate(d.scheduledAt)}
                          {d.jitterSeconds != null
                            && ` (held ${d.jitterSeconds}s so it does not read as a machine)`}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Dispatched</dt>
                        <dd>{d.dispatchedAt ? fmtRelative(d.dispatchedAt) : 'not yet'}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">On the platform</dt>
                        <dd>
                          {d.externalUrl
                            ? <a href={d.externalUrl} target="_blank" rel="noreferrer"
                                 className="text-primary underline">{d.externalPostId}</a>
                            : d.externalPostId ?? '—'}
                        </dd>
                      </div>
                    </dl>
                    {d.failureReason && (
                      <p className="mt-1 text-[10px] text-rose-600">{d.failureReason}</p>
                    )}
                    {d.attempts > 0 && (
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {d.attempts} send{d.attempts === 1 ? '' : 's'} attempted
                        {d.nextAttemptAt
                          ? ` · next ${fmtRelative(d.nextAttemptAt)}`
                          : d.statusCode === 'FAILED'
                            ? ' · stopped, waiting for a person'
                            : ''}
                      </p>
                    )}
                    <Link to={`/war-room/threads/${d.threadId}`}
                          className="mt-1 inline-block text-[11px] text-primary underline">
                      Open the thread it answered
                    </Link>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </ErpTable>
      </Panel>
      <OffsetPager page={page} totalPages={total.pages} totalElements={total.elements}
                   onPage={setPage} loading={false} unit="posts" />
    </div>
  )
}

/**
 * Search and paging for one of the war room's unbounded lists.
 *
 * <p>Threads, the approval queue, the ledger and coordination signals all accumulate for the
 * life of a campaign. The bounded lists — accounts, voices, watches, platforms — are
 * deliberately NOT paged: a room has a handful of each, and a pager that never has anything to
 * do is a control that teaches people to ignore controls.</p>
 */
function SearchBar({
  draft, onDraft, query, onSearch, onClear, total, unit,
}: {
  draft: string
  onDraft: (v: string) => void
  query: string
  onSearch: () => void
  onClear: () => void
  total: number
  unit: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input value={draft} onChange={(e) => onDraft(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') onSearch() }}
             placeholder="Search what was posted…"
             className="h-7 w-full max-w-xs rounded border border-input bg-background px-2
                        text-[12px]" />
      <button type="button" onClick={onSearch}
              className="rounded border border-border px-2 py-1 text-[11px]">Search</button>
      {query && (
        <button type="button" onClick={onClear}
                className="rounded border border-border px-2 py-1 text-[11px]">Clear</button>
      )}
      <span className="text-[10px] text-muted-foreground">
        {total} {unit}
      </span>
    </div>
  )
}
